import { diffWords } from 'diff';
import { useState } from 'react';

// ---------------------------------------------------------------------------
// WordDiff
// ---------------------------------------------------------------------------

function WordDiff({ prev, current }: { prev: string; current: string }) {
  const parts = diffWords(prev, current);
  return (
    <p className="text-[11.5px] leading-relaxed wrap-break-word whitespace-pre-wrap font-medium">
      {parts.map((part, i) =>
        part.removed ? (
          <span key={i} className="bg-red-100 text-red-700 line-through decoration-red-400 rounded-sm px-0.5">
            {part.value}
          </span>
        ) : part.added ? (
          <span key={i} className="bg-green-100 text-green-800 font-bold rounded-sm px-0.5">
            {part.value}
          </span>
        ) : (
          <span key={i} className="text-gray-700">
            {part.value}
          </span>
        )
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------
// DiffText
// ---------------------------------------------------------------------------

const COLLAPSE_THRESHOLD = 120;

function DiffText({
  current,
  prev,
  label,
  inDiffContext = false,
  forceExpand = false,
  wordDiff = false,
}: {
  current?: string | null;
  prev?: string | null;
  label: string;
  inDiffContext?: boolean;
  forceExpand?: boolean;
  wordDiff?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const hasValue = !!current;
  const hasPrev = inDiffContext && prev !== undefined && prev !== null && prev !== '';
  const isNew = inDiffContext && hasValue && !hasPrev;
  const hasDiff = inDiffContext && hasPrev && prev !== current;
  if (!current && !prev) return null;

  // El colapso/expansión gobierna TANTO el "después" (current) como el "antes" (prev).
  const isLong = (current?.length ?? 0) > COLLAPSE_THRESHOLD || (prev?.length ?? 0) > COLLAPSE_THRESHOLD;
  const isExpanded = forceExpand || expanded;
  const clip = (t?: string | null) => (isLong && !isExpanded && t ? t.slice(0, COLLAPSE_THRESHOLD) + '…' : t);
  const displayText = clip(current);
  const displayPrev = clip(prev);

  return (
    <div
      className={`mb-3.5 last:mb-0 transition-all ${
        hasDiff ? 'p-3 bg-blue-50/30 rounded-xl border border-blue-100/40 shadow-xs' : ''
      } ${isNew ? 'p-3 bg-green-50/40 rounded-xl border border-green-100/50 shadow-xs' : ''}`}
    >
      <div
        className={`text-[9px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-2 ${
          hasDiff ? 'text-blue-600' : isNew ? 'text-green-600' : 'text-gray-400'
        }`}
      >
        {label}
        {hasDiff && <span className="w-1 h-1 rounded-full bg-blue-400 animate-pulse" />}
        {isNew && (
          <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-green-100 text-green-700 text-[8px] font-black leading-none">
            + nuevo
          </span>
        )}
      </div>
      <div className="text-[11.5px] leading-relaxed wrap-break-word">
        {hasDiff && wordDiff && prev && current ? (
          <WordDiff prev={prev} current={current} />
        ) : (
          <>
            {hasDiff && prev && (
              <div className="mb-2">
                <span className="text-blue-300 line-through opacity-80 italic decoration-blue-200 block mb-0.5 wrap-break-word whitespace-pre-wrap">
                  {displayPrev}
                </span>
                <div className="h-px w-4 bg-blue-100 my-1" />
              </div>
            )}
            <p
              className={`wrap-break-word whitespace-pre-wrap ${hasDiff ? 'text-blue-800 font-bold' : isNew ? 'text-green-900 font-bold' : 'font-medium text-gray-600'}`}
            >
              {displayText || <span className="text-gray-300 italic">No registrado</span>}
            </p>
            {isLong && !forceExpand && (
              <button
                type="button"
                className="text-[9px] text-blue-500 hover:text-blue-700 mt-1 font-medium transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
              >
                {expanded ? 'Ver menos' : 'Ver más'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TernaFieldRevision
// ---------------------------------------------------------------------------

export type TernaFieldVeredictoEstado = 'APROBADO' | 'RECHAZADO' | 'COMENTADO';

export interface TernaFieldRevisionProps {
  evalTernaId: number;
  campo: 'observaciones' | 'planMejora' | 'fechaImplementacion';
  label: string;
  value?: string | null;
  prevValue?: string | null;
  inDiffContext?: boolean;
  forceExpand?: boolean;
  forceWordDiff?: boolean;
  /** @deprecated Verdict actions now live in RevisionFieldModal. Kept for backwards compat. */
  onVeredicto?: (
    evalTernaId: number,
    campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
    estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO',
    observacion?: string
  ) => void;
  /** @deprecated Replaced by onOpenReview. */
  onOpenComment?: (anchorEl: HTMLElement) => void;
  /** Opens the review modal for this field. */
  onOpenReview?: () => void;
  /** Veredicto en borrador de la vista paso a paso (TernaRevisionStepper), aún no guardado.
   * MatrizInline no usa esto — ahí cada Aprobar/Rechazar guarda de inmediato. */
  currentVeredicto?: { estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string };
  latestHistorial?: { estado: string; observacion?: string | null };
}

/**
 * Returns true when the field still requires a verdict from the admin.
 * PENDIENTE and absent estados are pending.
 * COMENTADO is also pending — it records a comment but does not close the review.
 */
export function isTernaFieldPendiente(estado: string | undefined): boolean {
  return !estado || estado === 'PENDIENTE' || estado === 'COMENTADO' || estado === 'RESPUESTA_ASESOR' || estado === 'RECHAZADO';
}

import { ShiftActionOverlay } from './ShiftActionOverlay';

export function TernaFieldRevision({
  evalTernaId,
  campo,
  label,
  value,
  prevValue,
  inDiffContext = false,
  forceExpand = false,
  forceWordDiff = false,
  onVeredicto,
  onOpenReview,
  currentVeredicto,
  latestHistorial,
}: TernaFieldRevisionProps) {
  if (!value && !prevValue) return null;

  const formatDateValue = (v: string | null | undefined) => {
    if (!v || campo !== 'fechaImplementacion') return v;
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const serverEstado = latestHistorial?.estado;
  const serverObs = latestHistorial?.observacion;

  const isServerRechazado = serverEstado === 'RECHAZADO';
  const isServerAprobado = serverEstado === 'APROBADO';
  const isPendiente = (serverEstado ? isTernaFieldPendiente(serverEstado) : inDiffContext) && !isServerAprobado && !isServerRechazado;
  const isAprobadoSinAccion = isServerAprobado || (!serverEstado && !inDiffContext);

  const estadoVigente = currentVeredicto?.estado ?? serverEstado;
  const accentClass =
    estadoVigente === 'APROBADO'
      ? 'border-l-4 border-l-[#2d7a4f]'
      : estadoVigente === 'RECHAZADO'
        ? 'border-l-4 border-l-[#c0392b]'
        : estadoVigente === 'COMENTADO'
          ? 'border-l-4 border-l-[#1d4ed8]'
          : onOpenReview
            ? 'border-l-4 border-l-gray-200'
            : '';

  const canReviewField = inDiffContext && Boolean(onVeredicto);
  const handleQuickAprobar = canReviewField && onVeredicto ? () => onVeredicto(evalTernaId, campo, 'APROBADO') : undefined;
  const handleQuickRechazar = canReviewField && onVeredicto ? () => onVeredicto(evalTernaId, campo, 'RECHAZADO') : undefined;

  return (
    <ShiftActionOverlay onAprobar={handleQuickAprobar} onRechazar={handleQuickRechazar}>
      <div
        role={onOpenReview ? 'button' : undefined}
        tabIndex={onOpenReview ? 0 : undefined}
        onClick={onOpenReview}
        onKeyDown={
          onOpenReview
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenReview();
                }
              }
            : undefined
        }
        className={`relative group mt-2 p-2 rounded-lg transition-colors duration-300 ${onOpenReview ? 'cursor-pointer' : ''} ${accentClass} ${
          inDiffContext
            ? 'bg-blue-50/25 border border-blue-200/50 hover:bg-blue-50/40 hover:border-blue-200/70'
            : 'bg-gray-50/50 border border-gray-100 hover:bg-gray-100/60'
        }`}
      >
        <div className="min-w-0">
          <DiffText
            current={formatDateValue(value)}
            prev={formatDateValue(prevValue)}
            label={label}
            inDiffContext={inDiffContext}
            forceExpand={forceExpand}
            wordDiff={campo === 'fechaImplementacion' ? false : forceWordDiff}
          />

          {currentVeredicto?.estado === 'APROBADO' && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] px-1.5 py-0.5 rounded-full font-semibold">
                Por aprobar <i className="not-italic text-[#2d7a4f]/60">*</i>
              </span>
              {currentVeredicto.observacion && (
                <span className="text-[11px] text-[#2d7a4f] italic block mt-0.5 whitespace-pre-wrap wrap-break-word">
                  "{currentVeredicto.observacion}"
                </span>
              )}
            </div>
          )}

          {currentVeredicto?.estado === 'RECHAZADO' && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] bg-[#fdf0ee] text-[#c0392b] border border-[#f5c6c0] px-1.5 py-0.5 rounded-full font-semibold">
                Por rechazar <i className="not-italic text-[#c0392b]/60">*</i>
              </span>
              {currentVeredicto.observacion && (
                <span className="text-[11px] text-[#c0392b] italic block mt-0.5 whitespace-pre-wrap wrap-break-word">
                  "{currentVeredicto.observacion}"
                </span>
              )}
            </div>
          )}

          {currentVeredicto?.estado === 'COMENTADO' && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] bg-[#eff6ff] text-[#1d4ed8] border border-[#bfdbfe] px-1.5 py-0.5 rounded-full font-semibold">
                Por comentar <i className="not-italic text-[#1d4ed8]/60">*</i>
              </span>
              {currentVeredicto.observacion && (
                <span className="text-[11px] text-[#1d4ed8] italic block mt-0.5 whitespace-pre-wrap wrap-break-word">
                  "{currentVeredicto.observacion}"
                </span>
              )}
            </div>
          )}

          {isServerRechazado && !currentVeredicto && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] bg-[#fdf0ee] text-[#c0392b] border border-[#f5c6c0] px-1.5 py-0.5 rounded-full font-semibold">
                Rechazado
              </span>
              {serverObs && (
                <span className="text-[11px] text-[#c0392b] italic block mt-0.5 whitespace-pre-wrap wrap-break-word">"{serverObs}"</span>
              )}
            </div>
          )}

          {isPendiente && !currentVeredicto && serverEstado !== 'COMENTADO' && (
            <span className="inline-flex items-center gap-1 text-[10px] bg-[#fef9c3] text-[#854d0e] border border-[#fef08a] px-1.5 py-0.5 rounded-full font-semibold mt-1">
              Pendiente
            </span>
          )}

          {isAprobadoSinAccion && !currentVeredicto && (
            <span className="inline-flex items-center gap-1 text-[10px] bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] px-1.5 py-0.5 rounded-full font-semibold mt-1">
              Aprobado
            </span>
          )}
        </div>
      </div>
    </ShiftActionOverlay>
  );
}
